import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'app/context';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { formatNum, formatNumTwoDig, formatPercent, formatPercentTwoDig, normalizeFilename } from 'app/utils';
import Panel from 'app/components/Panel';
import { ISankeyData, SankeyChart } from 'app/components/charts/SankeyChart';
import colors from 'assets/scss/colors.module.scss';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { ICampaignDetailData } from 'app/stores/ui/agt.akumaaktionen.ui.store';
import { Icon } from 'app/components/common/Icon';
import { TooltipPortal } from 'app/components/common/TooltipPortal';
import { Button } from 'app/components/common/Button';
import { PrintHeader } from 'app/components/common/TreePanelHeader';

interface IAkumaAktionenCampaignDetailPanelProps {
	campaignId: number;
	onClose: () => void;
}

export const AkumaAktionenCampaignDetailPanel = observer((props: IAkumaAktionenCampaignDetailPanelProps) => {
	const { campaignId, onClose } = props;
	const { agtAkumaAktionenUiStore, agenturListUiStore, pdfStore } = useStore();
	const [, setCampaignData] = useState<ICampaignDetailData[]>([]);
	const [loading, setLoading] = useState(true);
	const [campaign, setCampaign] = useState<any>(null);

	const tm = useMemo(() => {
		const tm = new TableModel<ICampaignDetailData, number>();
		const cols: ITableColumn<ICampaignDetailData, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		cols.push({
			label: 'Anzahl Aktionstreffer',
			path: 'anzahlAktionstreffer',
			sortBy: 'anzahlAktionstreffer',
			format: formatNum,
			agg: {
				aggType: AggregrationType.sum,
			},
		});

		cols.push({
			label: 'AMIS.NOW Aufgaben Agentur',
			path: 'zuweisungAgentur',
			sortBy: 'zuweisungAgentur',
			format: formatNum,
			agg: {
				aggType: AggregrationType.sum,
			},
		});

		cols.push({
			label: 'AMIS.NOW Aufgaben Mitarbeiter',
			path: 'zuweisungMitarbeiter',
			sortBy: 'zuweisungMitarbeiter',
			format: formatNum,
			agg: {
				aggType: AggregrationType.sum,
			},
		});

		cols.push({
			label: 'AMIS.NOW Aufgaben Gesamt',
			path: 'zuweisungGesamt',
			sortBy: 'zuweisungGesamt',
			format: formatNum,
			agg: {
				aggType: AggregrationType.sum,
			},
		});
		cols.push({
			label: 'Quote',
			path: 'bearbeitungsQuote',
			sortBy: 'bearbeitungsQuote',
			render: (row: TableRow<ICampaignDetailData>) => {
				const bqp = row.data.bearbeitungsQuotePass;
				return (
					<>
						{bqp && bqp >= 1 ? (
							<TooltipPortal tooltip={`Quote Erreicht`} position="left">
								<Icon iconClass="check-circle" />
							</TooltipPortal>
						) : (
							''
						)}{' '}
						{formatPercent(row.data.bearbeitungsQuote * 100)}%
					</>
				);
			},
			agg: {
				aggType: AggregrationType.avg,
			},
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);
		tm.idProperty = 'agt.agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		const loadCampaignData = async () => {
			setLoading(true);
			try {
				// Load campaign details
				const foundCampaign = agtAkumaAktionenUiStore.campaigns.find((c) => c.id === campaignId);
				setCampaign(foundCampaign);

				// Get actual campaign detail data from the store
				const campaignData: ICampaignDetailData[] = await agtAkumaAktionenUiStore.loadCampaignDetails(campaignId);

				setCampaignData(campaignData);
				tm.setRowData(campaignData);
				tm.sort();
			} catch (error) {
				console.error('Error loading campaign data:', error);
			} finally {
				setLoading(false);
			}
		};

		if (campaignId) {
			loadCampaignData();
		}
	}, [campaignId, tm, agtAkumaAktionenUiStore.campaigns, agenturListUiStore.items, agtAkumaAktionenUiStore.selectedKw]);

	const downloadPdf = () => {
		const currentPath = window.location.hash.substring(2);
		const fileName = campaign ? normalizeFilename(`Aktion-${campaign.campaignName}`) : `Aktion-${campaignId}`;
		pdfStore.printPdf2(currentPath, fileName);
	};

	const panelHeader = (
		<>
			<div className="campaign-info grid is-col-auto align-center">
				<Button className="button is-text " onClick={downloadPdf}>
					<span>Download PDF</span>
				</Button>
				{campaign && (
					<div className="tags has-addons">
						{campaign.topCampaign && <span className="tag is-green">Top Aktion</span>}
						{campaign.focusCampaign && <span className="tag is-primary">Focus Aktion</span>}
					</div>
				)}
			</div>
		</>
	);

	const campaignTitle = campaign ? `Aktion: ${campaign.campaignName}` : 'Aktion';
	return (
		<Panel title={campaignTitle} subtitle={`ID: ${campaignId}`} header={panelHeader} onClose={onClose} classModifier="is-campaign-detail">
			<PrintHeader title={campaignTitle} />
			<div className="panel-content table-container">
				{loading ? (
					<div className="has-text-centered pad-2rem">
						<div className="loader"></div>
						<p>Lade Aktion Details...</p>
					</div>
				) : (
					<>
						<Table stickyHeader={true} tm={tm} />
					</>
				)}
			</div>
		</Panel>
	);
});
